import axios from "axios";
import mslInstance from "../MSL/mslInstance";
import { apiRequest } from "../MSL/mslConfig";
import adminStatusService from "./adminStatusService";

let isLoggingOut = false;

export async function unifiedLogout(account, navigate) {
  if (isLoggingOut) return; // prevent multiple logouts
  isLoggingOut = true;

  const clearAppStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
    // Clear admin status cache on logout
    adminStatusService.clearCache();
  };

  try {
    const accounts = mslInstance.getAllAccounts();
    const acc = account || accounts[0];

    if (acc) {
      console.log("Logging out MSAL account:", acc.username);
      // Force logout even if account exists
      await mslInstance.logoutRedirect({
        account: acc,
        postLogoutRedirectUri: `${window.location.origin}/logout-success`,
      });
    } else {
      // Fallback logout
      clearAppStorage();
      if (navigate) {
        navigate("/", { replace: true });
      } else {
        window.location.replace("/");
      }
    }
  } catch (error) {
    console.error("Logout failed:", error);
    clearAppStorage();
    if (navigate) {
      navigate("/", { replace: true });
    } else {
      window.location.replace("/");
    }
  }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

const getAccessToken = async () => {
  if (isLoggingOut) return null;

  const accounts = mslInstance.getAllAccounts();
  const account = accounts[0];

  // 🔹 CHANGE #1 → use sessionStorage instead of in-memory flag
  const hasRedirected = localStorage.getItem("hasRedirected") === "true";

  if (!account) {
    console.warn("No account found");
    if (!hasRedirected) {
      localStorage.setItem("hasRedirected", "true");
      await mslInstance.loginRedirect(apiRequest);
    }
    return null;
  }

  try {
    const tokenResponse = await mslInstance.acquireTokenSilent({
      ...apiRequest,
      account,
      forceRefresh: true, // 🔹 CHANGE #3 → always refresh expired tokens
    });
    // 🔹 CHANGE #4 → reset redirect guard after successful login
    localStorage.removeItem("hasRedirected");

    return tokenResponse.accessToken;
  } catch (err) {
    console.warn("Silent login failed:", err);

    if (!hasRedirected) {
      console.log("Redirecting for interactive login...");
      localStorage.setItem("hasRedirected", "true");
      await mslInstance.loginRedirect(apiRequest);
    }
    return null;
  }
};

export async function initAuth() {
  if (isLoggingOut) return null;

  try {
    // Process redirect response (if user was redirected)
    const redirectResult = await mslInstance.handleRedirectPromise();
    if (redirectResult?.account) {
      mslInstance.setActiveAccount(redirectResult.account);
    } else {
      const existing = mslInstance.getAllAccounts();
      if (existing.length > 0) {
        mslInstance.setActiveAccount(existing[0]);
      }
    }
  } catch (err) {
    console.error("Redirect handling failed:", err);
  }

  const accounts = mslInstance.getAllAccounts();
  const account = accounts[0];
  const kmsiEnabled = localStorage.getItem("kmsi") === "true";

  // 🚩 FIX: If KMSI not enabled, don’t try redirect/silent → just return null
  if (!kmsiEnabled) {
    // console.log("KMSI not enabled → skipping silent login");
    return null;
  }

  // 🚩 If no account but KMSI is enabled → attempt redirect once
  if (!account) {
    // console.log("No account found, but KMSI enabled → redirecting...");
    await mslInstance.loginRedirect(apiRequest);
    return null;
  }

  try {
    // Try silent login
    const tokenResponse = await mslInstance.acquireTokenSilent({
      ...apiRequest,
      account,
      forceRefresh: true,
    });
    return tokenResponse;
  } catch (err) {
    console.warn("InitAuth silent login failed:", err);

    // 🚩 Don’t loop — only redirect if KMSI is enabled
    await mslInstance.loginRedirect(apiRequest);
    return null;
  }
}

api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    const detailCode = response?.data?.detail?.code;
    if (detailCode === 401 && !isLoggingOut) {
      unifiedLogout();
      return Promise.reject({ message: "User unauthorized", response });
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const detailCode = error.response?.data?.detail?.code;

    if ((status === 401 || detailCode === 401) && !isLoggingOut) {
      unifiedLogout();
    }

    return Promise.reject(error);
  }
);

export default api;
